/**
 * The choices of what to show in the Merge into... flow (D66), kept apart from
 * the screens so they can be tested alone.
 *
 * The backend decides and the app displays: nothing here works out a twin, a
 * count, a line or a word. A plant's twins are the ids the server sent in
 * GET /plants (`twins`); the variety question, the lines of what moves and the
 * crop names are the server's merge preview. These functions only pick which of
 * what was sent to draw, and which step comes first. Pure: it imports types and luxon only.
 *
 * Who uses it: dialogs/MergePlantSheet, lib/useMergeFlow and PlantCard.
 *
 * @module mergePlants
 */

import { DateTime } from 'luxon';
import type { Plant } from '@/shared/types/plant';
import type { MergePreview, MergeVarietyChoice } from '@/shared/api/plantMerge';

/** Where the merge sheet opens. */
export type MergeStep = 'none' | 'pick' | 'confirm';

/** One row of the pick step. */
export interface PickRow {
  /** The twin's id. */
  plantObjId: number;
  /** Its name as the server says it. */
  name: string;
  /** How many photos it has. */
  photos: number;
  /** How many notes it has. */
  notes: number;
  /** The day it was added, as the app's formatter wrote it. */
  added: string;
}

/** The prompt raised after an edit that made twins. */
export interface EditAsk {
  /** One twin, or several. */
  variant: 'one' | 'several';
  /** How many twins. */
  count: number;
  /** The twins, as plants of the list. */
  twins: Plant[];
}

/** One variety row of the confirm sheet. */
export interface VarietyRow {
  /** The variety's id. */
  varietyId: number;
  /** Its name; null for the placeholder (the sheet says "Not sure"). */
  name: string | null;
  /** True for the placeholder. */
  isPlaceholder: boolean;
  /** True for the server's suggestion. */
  selected: boolean;
}

/** What "Moves to the plant that stays" shows. */
export type MovesView = { kind: 'lines'; lines: string[] } | { kind: 'empty'; gone: string };

/**
 * A plant's twins as plants of the list, in the order the server sent the ids.
 * An id the list does not hold (a stale list) is left out, and so is the plant itself.
 *
 * @param plant - The plant.
 * @param plants - The garden's list.
 * @returns The twins held.
 */
export function twinsOfPlant(plant: Pick<Plant, 'plant_obj_id' | 'twins'>, plants: readonly Plant[]): Plant[] {
  const byId = new Map(plants.map((p) => [p.plant_obj_id, p]));
  const found: Plant[] = [];
  for (const id of plant.twins ?? []) {
    const twin = byId.get(id);
    if (twin && id !== plant.plant_obj_id) found.push(twin);
  }
  return found;
}

/**
 * Whether the card's menu offers Merge into...: a handler, a growing card and at least one twin.
 *
 * @param plant - The plant.
 * @param options - Whether the card is archived and whether a handler was given.
 * @returns True to show the item.
 */
export function canOfferMerge(
  plant: Pick<Plant, 'twins'>,
  options: { archived: boolean; hasHandler: boolean },
): boolean {
  return options.hasHandler && !options.archived && (plant.twins?.length ?? 0) > 0;
}

/**
 * Where the sheet opens: the pick step only with two or more twins, the confirm sheet at once with one.
 *
 * @param twins - The plant's twins.
 * @returns The first step.
 */
export function firstMergeStep(twins: readonly unknown[]): MergeStep {
  if (twins.length === 0) return 'none';
  return twins.length === 1 ? 'confirm' : 'pick';
}

/**
 * The name a plant is shown by in the sheets: the server's `names.shown`, else its full name.
 *
 * @param plant - The plant.
 * @returns The name.
 */
export function mergeName(plant: Pick<Plant, 'names'>): string {
  return plant.names.shown ?? plant.names.full;
}

/**
 * The day a plant was added, short and in the reader's language ("Jul 10"). The server stamps
 * `created_at` as SQL time in UTC ("2026-10-21 02:33:48"); an ISO string is read too.
 *
 * @param value - The plant's created_at.
 * @param locale - The reader's language ('en' or 'es').
 * @returns The day, or '' when the value is not a date.
 */
export function addedDay(value: string | null | undefined, locale: string): string {
  if (!value) return '';
  let parsed = DateTime.fromSQL(value, { zone: 'utc' });
  if (!parsed.isValid) parsed = DateTime.fromISO(value, { zone: 'utc' });
  if (!parsed.isValid) return '';
  return parsed.setLocale(locale).toLocaleString({ month: 'short', day: 'numeric' });
}

/**
 * One row per twin for the pick step.
 *
 * @param twins - The twins.
 * @param formatDay - The app's date formatter, given an ISO date.
 * @returns The rows.
 */
export function pickRows(twins: readonly Plant[], formatDay: (iso: string) => string): PickRow[] {
  return twins.map((twin) => ({
    plantObjId: twin.plant_obj_id,
    name: mergeName(twin),
    photos: twin.photos?.length ?? 0,
    notes: twin.notes?.length ?? 0,
    added: twin.created_at ? formatDay(twin.created_at) : '',
  }));
}

/**
 * The prompt the edit's caller raises once the form has closed: only after a successful edit that
 * answered twin ids the list holds.
 *
 * @param result - What editPlant answered.
 * @param plants - The garden's list.
 * @returns The prompt, or null for none.
 */
export function askAfterEdit(
  result: { success: boolean; twins?: number[] } | undefined,
  plants: readonly Plant[],
): EditAsk | null {
  if (!result || !result.success || !result.twins || result.twins.length === 0) return null;
  const byId = new Map(plants.map((p) => [p.plant_obj_id, p]));
  const twins = result.twins.map((id) => byId.get(id)).filter((p): p is Plant => p !== undefined);
  if (twins.length === 0) return null;
  return { variant: twins.length === 1 ? 'one' : 'several', count: twins.length, twins };
}

/**
 * The variety rows of the confirm sheet.
 *
 * @param choice - The preview's varietyChoice.
 * @returns The rows with the server's suggestion selected, or null when nothing is asked.
 */
export function varietyRows(choice: MergeVarietyChoice): VarietyRow[] | null {
  if (!choice.asked) return null;
  return choice.options.map((o) => ({
    varietyId: o.varietyId,
    name: o.name,
    isPlaceholder: o.isPlaceholder,
    selected: o.varietyId === choice.suggested,
  }));
}

/**
 * The variety Merge sends: the gardener's pick when it is one of the rows asked, else the server's suggestion.
 *
 * @param choice - The preview's varietyChoice.
 * @param picked - The row the gardener picked, if any.
 * @returns The variety id.
 */
export function varietyToSend(choice: MergeVarietyChoice, picked: number | null): number {
  if (choice.asked && picked !== null && choice.options.some((o) => o.varietyId === picked)) return picked;
  return choice.suggested;
}

/**
 * What the moves list shows.
 *
 * @param preview - The preview (its `empty`, `moves.lines` and `gone.name`).
 * @returns The server's lines as sent, or the one "Nothing to move" line for the plant that goes.
 */
export function movesView(
  preview: Pick<MergePreview, 'empty'> & { moves: Pick<MergePreview['moves'], 'lines'>; gone: Pick<MergePreview['gone'], 'name'> },
): MovesView {
  if (preview.empty) return { kind: 'empty', gone: preview.gone.name };
  return { kind: 'lines', lines: preview.moves.lines };
}

/**
 * The query key of one pair's preview.
 *
 * @param goneId - The plant that goes.
 * @param keepId - The plant that stays.
 * @returns The key.
 */
export function previewKey(goneId: number, keepId: number): ['merge-preview', number, number] {
  return ['merge-preview', goneId, keepId];
}

/**
 * The queries to refresh once a merge is done (the list itself is refetched through the plants context).
 *
 * @param keepId - The plant that stays.
 * @returns The keys to invalidate.
 */
export function afterMergeKeys(keepId: number): { invalidate: Array<Array<string | number>> } {
  return { invalidate: [['notes', 'plant', keepId], ['plant-sharing', keepId]] };
}

/**
 * How a failed request is worded: a refusal (400, 404, 409) shows the server's own message; a
 * failure on our side, or no answer, shows the standard sentence.
 *
 * @param status - The HTTP status, or nothing when there was no answer.
 * @returns 'refusal' or 'standard'.
 */
export function failureKind(status: number | null | undefined): 'refusal' | 'standard' {
  return status === 400 || status === 404 || status === 409 ? 'refusal' : 'standard';
}
